import React, { useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ClassContext } from '../context/ClassContext';
import GamificationStats from './GamificationStats';
import QuizPerformanceChart from './QuizPerformanceChart';
import FeeTracking from './FeeTracking';

const StudentDashboard = () => {
    const { classes, getClasses, loading } = useContext(ClassContext);

    useEffect(() => {
        getClasses();
    }, [getClasses]);

    // Debug: log what classes actually is
    console.log('StudentDashboard - classes:', classes, 'type:', typeof classes, 'isArray:', Array.isArray(classes));

    // Ensure classes is always an array
    const classesArray = Array.isArray(classes) ? classes : [];

    const formatSchedules = (schedules) => {
        if (!schedules || schedules.length === 0) return 'Chưa có lịch học';

        const dayLabels = {
            'monday': 'Thứ Hai',
            'tuesday': 'Thứ Ba',
            'wednesday': 'Thứ Tư',
            'thursday': 'Thứ Năm',
            'friday': 'Thứ Sáu',
            'saturday': 'Thứ Bảy',
            'sunday': 'Chủ Nhật'
        };

        return schedules.map(schedule =>
            `${dayLabels[schedule.dayOfWeek]} ${schedule.startTime}-${schedule.endTime}`
        ).join(', ');
    };

    return (
        <div className="space-y-10">
            {/* Gamification Section */}
            <div className="grid gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <GamificationStats />
                </div>
                <div>
                    <QuizPerformanceChart />
                </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-3">
                <div className="space-y-6 rounded-3xl bg-white/90 p-8 shadow-xl lg:col-span-2">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="flex items-center gap-3 text-2xl font-semibold text-slate-900">
                            <span aria-hidden>📖</span>
                            Lớp học của tôi
                        </h3>
                        <div className="flex flex-wrap gap-3">
                            <Link
                                to="/calendar"
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-600 transition hover:bg-indigo-100"
                            >
                                📅 Xem lịch học
                            </Link>
                            <Link
                                to="/quizzes"
                                className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-4 py-2 text-sm font-medium text-orange-600 transition hover:bg-orange-100"
                            >
                                📝 Bài tập
                            </Link>
                            <Link
                                to="/custom-quizzes"
                                className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 text-sm font-medium text-purple-600 transition hover:bg-purple-100"
                            >
                                ✏️ Tạo bài tập
                            </Link>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 text-slate-500">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-500" aria-hidden />
                            <span>Đang tải lịch học của bạn...</span>
                        </div>
                    ) : classesArray.length === 0 ? (
                        <div className="flex flex-col items-center gap-4 rounded-2xl bg-slate-50 p-10 text-center">
                            <div className="text-5xl" aria-hidden>📚</div>
                            <h4 className="text-xl font-semibold text-slate-900">Bạn chưa đăng ký lớp học nào</h4>
                            <p className="max-w-md text-sm text-slate-500">
                                Hãy liên hệ với giáo viên để được thêm vào lớp học.
                            </p>
                        </div>
                    ) : (
                        <div className="grid gap-5 md:grid-cols-2">
                            {classesArray.map((c) => (
                                <div
                                    key={c._id}
                                    className="flex h-full flex-col rounded-2xl border border-slate-100 bg-white/80 p-6 shadow-lg transition hover:-translate-y-1 hover:border-indigo-200 hover:shadow-xl"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <h4 className="text-lg font-semibold text-slate-900">{c.name}</h4>
                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                                            ✅ Đang học
                                        </span>
                                    </div>
                                    <p className="mt-4 text-sm text-slate-600">
                                        📅 {formatSchedules(c.schedules || []) || c.schedule}
                                    </p>
                                    <div className="mt-auto pt-6">
                                        <Link
                                            to={`/class/${c._id}`}
                                            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                                        >
                                            📚 Xem tài liệu
                                        </Link>
                                    </div>
                                    <div className="mt-4 text-xs text-slate-400">📊 Cập nhật: Hôm nay</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    <FeeTracking />

                    <div className="flex flex-col justify-between gap-6 rounded-3xl bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-600 p-8 text-white shadow-2xl">
                        <div className="space-y-4">
                            <h3 className="text-2xl font-semibold">💪 Tiếp tục phấn đấu!</h3>
                            <p className="text-sm text-indigo-100">
                                Mỗi bài học là một bước tiến trong hành trình tri thức của bạn. Hãy đặt mục tiêu nhỏ mỗi ngày và cùng nhau thực hiện chúng.
                            </p>
                        </div>
                        <Link
                            to="/quizzes"
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/15 px-6 py-3 text-sm font-semibold backdrop-blur transition hover:bg-white/25"
                        >
                            🚀 Bắt đầu làm bài tập
                        </Link>
                        <div className="flex items-center justify-center gap-3 rounded-2xl bg-white/10 px-5 py-4">
                            <span className="text-3xl" aria-hidden>🏆</span>
                            <div className="text-sm font-medium text-indigo-50">Học viên tích cực</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentDashboard;
